//
// Map store
//
// Manages all map related settings such as styles, historic overlays
// and marker collections.
//

import { LOGOUT } from './auth';

// ------------------------------------
// Styles
// ------------------------------------

export const MAP_DEFAULT = 'amsterdammuseum/cjcfzoq3s5wd92rqqs46vqzs9';
export const MAP_HISTORY = 'amsterdammuseum/cjdvex4wg626l2snnj11sb6u9';
export const MAP_SATELLITE = 'mapbox/satellite-v9';

// ------------------------------------
// Map position/zoom
// ------------------------------------

export const MAP_DEFAULT_CENTER = [52.3728, 4.8895];
export const MAP_DEFAULT_ZOOM = 14;

// used to zoom to 'world view'
export const MAP_WORLD_CENTER = [1.7400339, -16.2444486];
export const MAP_WORLD_ZOOM = 2.5;

// ------------------------------------
// Overlays
// ------------------------------------

export const MAP_1560 = 'oudestad_1560';
// export const MAP_1657 = 'grachtengordel_1657';
// export const MAP_1717 = 'amsterdam_en_omgeving_1717';
// export const MAP_1780 = 'grachtengordel_1780';
// export const MAP_1893 = 'amsterdam_omgeving_1893';
// export const MAP_1938 = 'amsterdam_1938';
// export const MAP_1970 = 'amsterdam_1970';

// ------------------------------------
// Collections
// ------------------------------------

export const ARCHEOLOGY = 'Archeologie';
export const ARCHITECTURE = 'Architectuur';
export const AMSTERDAM_MUSEUM = 'Museumschatten';
export const MONUMENTS = 'Monumenten';
export const ARCHIVE = 'Verhalen';
export const CBK = 'Buitenkunst';
export const WERELDERFGOED = 'Werelderfgoed';


// special collections for slide shows
export const COLLECTION_SUIKER = 'Suiker';
export const COLLECTION_ENDANGERED_SPECIES = 'Bedreigde Diersoorten';
export const COLLECTION_CLIMATE = 'Klimaat';

// ------------------------------------
// Themes
// ------------------------------------

export const THEME_17TH_CENTURY = '17e eeuw';
export const THEME_WOII = 'Tweede Wereldoorlog';
export const THEME_LIVING = 'Wonen';
export const THEME_GREEN = 'Groen';
export const THEME_WATER = 'Water';
export const THEME_SPORTS = 'Sport en spel';
export const THEME_SLAVERY = 'Slavernij';
export const THEME_RELIGION = 'Religie';
export const THEME_TRANSPORT = 'Vervoer';
export const THEME_REMBRANDT = 'Special: Rembrandt';
export const THEME_MIDDLE_AGES = 'Special: Middeleeuwen';
export const THEME_SCHOOLTUINEN = 'Special: Schooltuinen';
export const THEME_WERELDGRACHTEN = 'Special: Wereldgrachten';
export const THEME_CANON_VAN_AMSTERDAM = 'Special: Canon van Amsterdam'

// ------------------------------------
// Action definitions
// ------------------------------------

export const SET_STYLE = 'MAP/SET_STYLE';
export const SET_VIEWPORT = 'MAP/SET_VIEWPORT';
export const SET_OVERLAY = 'MAP/SET_OVERLAY';
export const ADD_COLLECTION = 'MAP/ADD_COLLECTION';
export const REMOVE_COLLECTION = 'MAP/REMOVE_COLLECTION';
export const SET_COLLECTIONS = 'MAP/SET_COLLECTIONS';
export const ADD_THEME = 'MAP/ADD_THEME';
export const REMOVE_THEME = 'MAP/REMOVE_THEME';
export const SET_THEMES = 'MAP/SET_THEMES';
export const ADD_LINE = 'MAP/ADD_LINE';
export const REMOVE_LINE = 'MAP/REMOVE_LINE';
export const CLEAR_CANVAS = 'MAP/CLEAR_CANVAS';
export const IMPORT = 'MAP/IMPORT';
export const RESET = 'MAP/RESET';
export const EXPAND_THEME_CATEGORY = 'MAP/EXPAND_THEME_CATEGORY';
export const COLLAPSE_THEME_CATEGORY = 'MAP/COLLAPSE_THEME_CATEGORY';

// ------------------------------------
// Action creators
// ------------------------------------

export const setStyle = style => ({
  type: SET_STYLE,
  style
});

export const setViewport = ({ center, zoom }) => ({
  type: SET_VIEWPORT,
  center,
  zoom
});

export const setOverlay = overlay => ({
  type: SET_OVERLAY,
  overlay
});

export const addCollection = collection => ({
  type: ADD_COLLECTION,
  collection
});

export const removeCollection = collection => ({
  type: REMOVE_COLLECTION,
  collection
});

export const setCollections = collections => ({
  type: SET_COLLECTIONS,
  collections
});

export const toggleCollection = collection => (dispatch, getState) => {
  const { map: { collections } } = getState();

  collections.includes(collection)
    ? dispatch(removeCollection(collection))
    : dispatch(addCollection(collection));
};

export const addTheme = theme => ({
  type: ADD_THEME,
  theme
});

export const removeTheme = theme => ({
  type: REMOVE_THEME,
  theme
});

export const setThemes = themes => ({
  type: SET_THEMES,
  themes
});

export const expandThemeCategory = category => ({
  type: EXPAND_THEME_CATEGORY,
  category
});

export const collapseThemeCategory = category => ({
  type: COLLAPSE_THEME_CATEGORY,
  category
});

export const toggleTheme = theme => (dispatch, getState) => {
  const { map: { themes } } = getState();

  themes.includes(theme)
    ? dispatch(removeTheme(theme))
    : dispatch(addTheme(theme));
};

export const toggleThemeCategory = category => (dispatch, getState) => {
  const { map: { expandedThemeCategories } } = getState();

  expandedThemeCategories.includes(category)
    ? dispatch(collapseThemeCategory(category))
    : dispatch(expandThemeCategory(category));
};

export const addLine = (coordinates, color) => ({
  type: ADD_LINE,
  coordinates,
  color
});

export const removeLine = index => ({
  type: REMOVE_LINE,
  index
});

export const clearCanvas = () => ({
  type: CLEAR_CANVAS
});

export const importMap = (state = {}) => ({
  type: IMPORT,
  state
});

export const resetMap = () => ({
  type: RESET
});

// ------------------------------------
// Initial state
// ------------------------------------

const initialState = {
  style: MAP_DEFAULT,
  center: MAP_DEFAULT_CENTER,
  zoom: MAP_DEFAULT_ZOOM,
  overlay: MAP_1560,
  collections: [],
  themes: [],
  polylines: [],
  expandedThemeCategories: [],
};

// ------------------------------------
// Reducer
// ------------------------------------

export default (state = initialState, action) => {
  switch (action.type) {
    case SET_STYLE: {
      const { style } = action;
      return { ...state, style };
    }

    case SET_VIEWPORT: {
      const { center, zoom } = action;
      return { ...state, center, zoom };
    }

    case SET_OVERLAY: {
      return {
        ...state,
        overlay: action.overlay
      };
    }

    case ADD_COLLECTION: {
      const collections = [
        ...state.collections,
        action.collection
      ];

      return { ...state, collections, themes: [] };
    }

    case REMOVE_COLLECTION: {
      const collections = state.collections.filter(collection =>
        collection !== action.collection
      );

      return { ...state, collections, themes: [] };
    }

    case SET_COLLECTIONS: {
      return { ...state, collections: action.collections, themes: [] };
    }

    case ADD_THEME: {
      const themes = [
        ...state.themes,
        action.theme
      ];

      return { ...state, themes, collections: [] };
    }

    case REMOVE_THEME: {
      const themes = state.themes.filter(theme =>
        theme !== action.theme
      );

      return { ...state, themes, collections: [] };
    }

    case SET_THEMES: {
      return { ...state, themes: action.themes, collections: [] };
    }

    case EXPAND_THEME_CATEGORY: {
      const expandedThemeCategories = [
        ...state.expandedThemeCategories,
        action.category
      ];

      return { ...state, expandedThemeCategories };
    }

    case COLLAPSE_THEME_CATEGORY: {
      const expandedThemeCategories = state.expandedThemeCategories.filter(category =>
        category !== action.category
      );

      return { ...state, expandedThemeCategories };
    }

    case ADD_LINE: {
      const { coordinates, color } = action;
      const polylines = [...state.polylines];

      polylines.push({
        coordinates,
        color
      });

      return { ...state, polylines };
    }

    case REMOVE_LINE: {
      const polylines = state.polylines.filter((polyline, index) =>
        index !== action.index
      );

      return { ...state, polylines };
    }

    case CLEAR_CANVAS: {
      return { ...state, polylines: [] };
    }

    case IMPORT: {
      const { state } = action;
      return { ...initialState, ...state };
    }

    case RESET:
    case LOGOUT:
      return initialState;

    default:
      return state
  }
};



// WEBPACK FOOTER //
// ./src/store/modules/map.js